import { cva } from 'class-variance-authority';

/**
 * Compound variants para los diferentes colores del RadioButton
 * Basado en tokens semánticos de Figma
 */
export const compoundVariants = [
  // ==================== BLUE VARIANTS ====================
  {
    color: 'blue',
    state: 'checked',
    class: 'ui-border-4 ui-border-fill-brand ui-bg-fill',
  },
  {
    color: 'blue',
    state: 'checkedHover',
    class: 'ui-border-4 ui-border-blue-600 ui-bg-neutral-50',
  },
  {
    color: 'blue',
    state: 'checkedActive',
    class: 'ui-border-4 ui-border-focus ui-bg-fill ui-shadow-01',
  },
  {
    color: 'blue',
    state: 'disabledChecked',
    class: 'ui-border-4 ui-border-focus ui-bg-fill',
  },

  // ==================== GREEN VARIANTS ====================
  {
    color: 'green',
    state: 'checked',
    class: 'ui-border-4 ui-border-success ui-bg-fill',
  },
  {
    color: 'green',
    state: 'checkedHover',
    class: 'ui-border-4 ui-border-green-600 ui-bg-neutral-50',
  },
  {
    color: 'green',
    state: 'checkedActive',
    class: 'ui-border-4 ui-border-success ui-bg-fill ui-shadow-01',
  },
  {
    color: 'green',
    state: 'disabledChecked',
    class: 'ui-border-4 ui-border-green-300 ui-bg-fill',
  },

  // ==================== RED VARIANTS ====================
  {
    color: 'red',
    state: 'checked',
    class: 'ui-border-4 ui-border-danger ui-bg-fill',
  },
  {
    color: 'red',
    state: 'checkedHover',
    class: 'ui-border-4 ui-border-red-600 ui-bg-neutral-50',
  },
  {
    color: 'red',
    state: 'checkedActive',
    class: 'ui-border-4 ui-border-danger ui-bg-fill ui-shadow-01',
  },
  {
    color: 'red',
    state: 'disabledChecked',
    class: 'ui-border-4 ui-border-red-300 ui-bg-fill',
  },

  // ==================== YELLOW VARIANTS ====================
  {
    color: 'yellow',
    state: 'checked',
    class: 'ui-border-4 ui-border-warning ui-bg-fill',
  },
  {
    color: 'yellow',
    state: 'checkedHover',
    class: 'ui-border-4 ui-border-yellow-600 ui-bg-neutral-50',
  },
  {
    color: 'yellow',
    state: 'checkedActive',
    class: 'ui-border-4 ui-border-warning ui-bg-fill ui-shadow-01',
  },
  {
    color: 'yellow',
    state: 'disabledChecked',
    class: 'ui-border-4 ui-border-yellow-300 ui-bg-fill',
  },

  // ==================== NEUTRAL VARIANTS ====================
  {
    color: 'neutral',
    state: 'checked',
    class: 'ui-border-4 ui-border-pressed ui-bg-fill',
  },
  {
    color: 'neutral',
    state: 'checkedHover',
    class: 'ui-border-4 ui-border-neutral-600 ui-bg-neutral-50',
  },
  {
    color: 'neutral',
    state: 'checkedActive',
    class: 'ui-border-4 ui-border-pressed ui-bg-fill ui-shadow-01',
  },
  {
    color: 'neutral',
    state: 'disabledChecked',
    class: 'ui-border-4 ui-border-neutral-300 ui-bg-fill',
  },
];

/**
 * Variantes del RadioButton usando tokens semánticos de Figma
 *
 * Tamaños basados en --ui-size-control-*:
 * - sm: 12px (control-xs)
 * - md: 16px (control-sm)
 * - lg: 20px (control-md)
 *
 * Estados basados en tokens semánticos:
 * - border: --ui-color-border-*
 * - bg: --ui-color-bg-fill-*
 */
export const radioButtonVariants = cva(
  [
    'ui-relative',
    'ui-rounded-control-pill',
    'ui-flex',
    'ui-items-center',
    'ui-justify-center',
    'ui-box-border',
    'ui-transition-all',
    'ui-duration-100',
    'ui-ease-in-out',
  ],
  {
    variants: {
      size: {
        xs: 'ui-w-16 ui-h-16 ui-min-w-16 ui-min-h-16',
        sm: 'ui-w-control-xs ui-h-control-xs ui-min-w-control-xs ui-min-h-control-xs',
        md: 'ui-w-control-sm ui-h-control-sm ui-min-w-control-sm ui-min-h-control-sm',
        lg: 'ui-w-control-md ui-h-control-md ui-min-w-control-md ui-min-h-control-md',
      },
      color: {
        blue: '',
        green: '',
        red: '',
        yellow: '',
        neutral: '',
      },
      state: {
        // Unchecked states - usan border-1 (default de Figma)
        unchecked: 'ui-border-1 ui-border-solid ui-border-DEFAULT ui-bg-fill',
        uncheckedHover: 'ui-border-1 ui-border-solid ui-border-hover ui-bg-fill-hover',
        uncheckedActive: 'ui-border-1 ui-border-solid ui-border-pressed ui-bg-fill-pressed',
        disabledUnchecked:
          'ui-border-1 ui-border-solid ui-border-disabled ui-bg-fill-disabled ui-cursor-not-allowed ui-opacity-100',

        // Checked states - usan border-4 (definido en compoundVariants por color)
        checked: '',
        checkedHover: '',
        checkedActive: '',
        disabledChecked: 'ui-cursor-not-allowed ui-opacity-60',
      },
    },
    compoundVariants: compoundVariants as unknown as Array<{
      color: 'blue' | 'green' | 'red' | 'yellow' | 'neutral';
      state: 'checked' | 'checkedHover' | 'checkedActive' | 'disabledChecked';
      class: string;
    }>,
    defaultVariants: {
      size: 'md',
      color: 'blue',
      state: 'unchecked',
    },
  }
);

/**
 * Variantes del label usando tipografía semántica de Figma
 *
 * Tamaños basados en tokens de tipografía:
 * - sm: body-xs
 * - md: body-sm
 * - lg: body-md
 */
export const labelVariants = cva(
  ['ui-ml-2', 'ui-select-none', 'ui-text-primary', 'ui-transition-colors', 'ui-duration-100'],
  {
    variants: {
      size: {
        xs: 'ui-text-label-md-regular',
      },
      disabled: {
        true: 'ui-cursor-not-allowed ui-text-disabled',
        false: '',
      },
    },
    defaultVariants: {
      size: 'xs',
      disabled: false,
    },
  }
);
